import { Component, OnDestroy, OnInit, AfterViewInit } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { ResolutionNavHelper } from '../../service/core/resolution-nav-helper.service';
import { NavigationService } from '../../service/core/navigation.service';
import { selectPosDisplayActivated, selectPosModifyActivated } from '../../store/pos/pos-selector';
import { selectMarketsDisplayActivated, selectMarketsModifyActivated } from '../../store/markets/markets-selector';
import { selectHotelModifyActivated, selectHotelRuleDisplayActivated } from '../../store/hotel/hotels-selector';
import { selectFlightRuleDisplayActivated, selectFlightModifyActivated } from '../../store/flight/flights-selector';
import { selectFamilyModifyActivated, selectFamilyRuleDisplayActivated } from '../../store/families/families-selector';
import { selectFareModifyActivated, selectFareRuleDisplayActivated } from '../../store/fares/fares-selector';
import { Subscription } from 'rxjs';
import { startWith, distinctUntilChanged } from 'rxjs/operators';
import { RouteURL } from '../../model/route-url';
import { selectLastActiveAirFamilyView } from '../../store/families/air/air-families-selector';
import { UppViewNames, ViewMode } from '../../service/model';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { ConfigurationService } from '../../service/configuration/configuration.service';
import { FeatureFlags } from '../util/resources';
import { DfSideNavItem, DfSideNavService, DfSideNavModule } from '@design-factory/design-factory';
import { NavigationMenuIdentifiers } from '../../service/core/navigation-menu-identifiers';

@Component({
  selector: 'ama-ng-upp-navigation-menu',
  templateUrl: './navigation-menu.component.html',
  styleUrls: ['./navigation-menu.component.scss'],
  standalone: true,
  imports: [NgbCollapse, RouterLink, RouterLinkActive, NgTemplateOutlet, DfSideNavModule, CommonModule]
})
export class NavigationMenuComponent implements OnInit, AfterViewInit, OnDestroy {
  posDisplayViewEnabled = false;
  posModifyViewEnabled = false;

  marketsDisplayViewEnabled = false;
  marketsModifyViewEnabled = false;

  hotelsDisplayViewEnabled = false;
  hotelsModifyViewEnabled = false;

  flightsDisplayViewEnabled = false;
  flightsModifyViewEnabled = false;

  familiesDisplayViewEnabled = false;
  familiesModifyViewEnabled = false;

  faresDisplayViewEnabled = false;
  faresModifyViewEnabled = false;

  subscription: Subscription = new Subscription();

  RouteURL = RouteURL;
  lastActiveAirFamilyView: ViewMode | null = null;
  UppViewNames = UppViewNames;

  ruleHistoryActivate = false;
  useDfSideNav: boolean | undefined = true;
  navInitialized = true;

  private readonly ICON_CREATE = 'fa-file-signature';
  private readonly ICON_DISPLAY = 'fa-file-alt';
  private readonly ICON_MODIFY = 'fa-file-edit';

  constructor(
    readonly resolutionNavHelper: ResolutionNavHelper,
    readonly navigationService: NavigationService,
    private readonly store: Store,
    private readonly configurationService: ConfigurationService,
    readonly sideNavService: DfSideNavService
  ) {}

  ngOnInit(): void {
    this.subscription?.add(
      this.navigationService.menuStateChanged$.subscribe(() => {
        if (this.useDfSideNav) {
          this.updateDfSideNavItems();
        }
      })
    );

    this.subscription?.add(
      this.configurationService
        .getParameter$(FeatureFlags.showDFSideNav)
        .pipe(startWith(true), distinctUntilChanged())
        .subscribe((showDFSidenav = false) => {
          this.useDfSideNav = showDFSidenav;
          if (this.useDfSideNav) {
            this.updateDfSideNavItems();
          }
        })
    );

    this.subscription?.add(
      this.store.pipe(select(selectPosModifyActivated)).subscribe((activated) => {
        this.posModifyViewEnabled = activated;
        if (this.useDfSideNav) {
          this.updateDfSideNavItems();
        }
      })
    );

    this.subscription?.add(
      this.store.pipe(select(selectPosDisplayActivated)).subscribe((activated) => {
        this.posDisplayViewEnabled = activated;
        if (this.useDfSideNav) {
          this.updateDfSideNavItems();
        }
      })
    );

    this.subscription?.add(
      this.store.pipe(select(selectMarketsDisplayActivated)).subscribe((activated) => {
        this.marketsDisplayViewEnabled = activated;
        if (this.useDfSideNav) {
          this.updateDfSideNavItems();
        }
      })
    );

    this.subscription?.add(
      this.store.pipe(select(selectMarketsModifyActivated)).subscribe((activated) => {
        this.marketsModifyViewEnabled = activated;
        if (this.useDfSideNav) {
          this.updateDfSideNavItems();
        }
      })
    );

    this.subscription?.add(
      this.store.pipe(select(selectHotelRuleDisplayActivated)).subscribe((activated) => {
        this.hotelsDisplayViewEnabled = activated;
        if (this.useDfSideNav) {
          this.updateDfSideNavItems();
        }
      })
    );

    this.subscription?.add(
      this.store.pipe(select(selectHotelModifyActivated)).subscribe((activated) => {
        this.hotelsModifyViewEnabled = activated;
        if (this.useDfSideNav) {
          this.updateDfSideNavItems();
        }
      })
    );

    this.subscription?.add(
      this.store.pipe(select(selectFlightRuleDisplayActivated)).subscribe((activated) => {
        this.flightsDisplayViewEnabled = activated;
        if (this.useDfSideNav) {
          this.updateDfSideNavItems();
        }
      })
    );

    this.subscription?.add(
      this.store.pipe(select(selectFlightModifyActivated)).subscribe((activated) => {
        this.flightsModifyViewEnabled = activated;
        if (this.useDfSideNav) {
          this.updateDfSideNavItems();
        }
      })
    );

    this.subscription?.add(
      this.store.pipe(select(selectFamilyRuleDisplayActivated)).subscribe((activated) => {
        this.familiesDisplayViewEnabled = activated;
        if (this.useDfSideNav) {
          this.updateDfSideNavItems();
        }
      })
    );

    this.subscription?.add(
      this.store.pipe(select(selectFamilyModifyActivated)).subscribe((activated) => {
        this.familiesModifyViewEnabled = activated;
        if (this.useDfSideNav) {
          this.updateDfSideNavItems();
        }
      })
    );

    this.subscription?.add(
      this.store.pipe(select(selectFareRuleDisplayActivated)).subscribe((activated) => {
        this.faresDisplayViewEnabled = activated;
        if (this.useDfSideNav) {
          this.updateDfSideNavItems();
        }
      })
    );

    this.subscription?.add(
      this.store.pipe(select(selectFareModifyActivated)).subscribe((activated) => {
        this.faresModifyViewEnabled = activated;
        if (this.useDfSideNav) {
          this.updateDfSideNavItems();
        }
      })
    );

    this.subscription?.add(
      this.store.pipe(select(selectLastActiveAirFamilyView)).subscribe((view) => {
        this.lastActiveAirFamilyView = view;
        if (this.useDfSideNav) {
          this.updateDfSideNavItems();
        }
      })
    );

    this.subscription?.add(
      this.configurationService.getParameter$(FeatureFlags.ruleHistory).subscribe((ruleHistory) => {
        this.ruleHistoryActivate = ruleHistory ?? false;
        if (this.useDfSideNav) {
          this.updateDfSideNavItems();
        }
      })
    );
  }

  ngAfterViewInit(): void {
    this.resolutionNavHelper.updateTitleVisibility();
    // Ensure nav items are initialized after router has processed the current route
    if (this.useDfSideNav) {
      setTimeout(() => this.updateDfSideNavItems(), 0);
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  isMenuCollapsed() {
    return this.resolutionNavHelper.isMenuCollapsed;
  }

  hideSideNav() {
    this.resolutionNavHelper.hideSideNav();
  }

  isPosCollapsed() {
    return this.navigationService.isPosCollapsed;
  }

  isMarketsCollapsed() {
    return this.navigationService.isMarketsCollapsed;
  }

  isHotelsCollapsed() {
    return this.navigationService.isHotelsCollapsed;
  }

  isFlightsCollapsed() {
    return this.navigationService.isFlightsCollapsed;
  }

  isFaresCollapsed() {
    return this.navigationService.isFaresCollapsed;
  }

  togglePos() {
    return this.navigationService.togglePos();
  }

  toggleMarkets() {
    return this.navigationService.toggleMarkets();
  }

  toggleHotels() {
    return this.navigationService.toggleHotels();
  }

  toggleFlights() {
    return this.navigationService.toggleFlights();
  }

  toggleFamilies() {
    return this.navigationService.toggleFamilies();
  }

  toggleFares() {
    return this.navigationService.toggleFares();
  }

  isNavigationDisabled() {
    return this.navigationService.isNavigationDisabled();
  }

  private updateDfSideNavItems(): void {
    this.navigationService.syncFromDfSideNavItems(this.sideNavService.items || []);

    const newItems = this.buildNavItems();
    this.sideNavService.initItems(newItems, true);
  }

  /**
   * Builds nav items array - can be called reactively as state changes
   * This replaces the need for storing navItems and rebuilding on every subscription
   */
  private buildNavItems(): DfSideNavItem[] {
    const navItems: DfSideNavItem[] = [];

    this.addDashboardItem(navItems);
    this.addPosMenuItem(navItems);
    this.addMarketsMenuItem(navItems);
    this.addFamiliesMenuItem(navItems);
    this.addFaresMenuItem(navItems);
    this.addFlightsMenuItem(navItems);
    this.addHotelsMenuItem(navItems);
    this.addAboutItem(navItems);

    return navItems;
  }

  private addDashboardItem(navItems: DfSideNavItem[]): void {
    navItems.push({
      value: '/dashboard',
      title: 'Dashboard',
      icon: 'fa-tachometer'
    });
  }

  private addPosMenuItem(navItems: DfSideNavItem[]): void {
    if (!this.navigationService.posViewEnabled) {
      return;
    }

    const posSubMenus: DfSideNavItem[] = [
      {
        value: '/pos/search',
        title: 'Search',
        icon: 'fa-search'
      },
      {
        value: '/pos/create',
        title: 'Create',
        icon: this.ICON_CREATE,
        class: !this.navigationService.posManageEnabled ? 'disabled' : ''
      },
      {
        value: this.posModifyViewEnabled ? '/pos/modify' : '/pos/display',
        title: 'Display',
        icon: this.posModifyViewEnabled ? this.ICON_MODIFY : this.ICON_DISPLAY,
        class: !this.posDisplayViewEnabled ? 'disabled' : ''
      }
    ];

    navItems.push({
      value: NavigationMenuIdentifiers.POS,
      title: 'Points Of Sale',
      icon: 'fa-cash-register',
      isCollapsed: this.navigationService.isPosCollapsed,
      subMenus: posSubMenus
    });
  }

  private addMarketsMenuItem(navItems: DfSideNavItem[]): void {
    if (!this.navigationService.marketsViewEnabled) {
      return;
    }

    const marketsSubMenus: DfSideNavItem[] = [
      {
        value: '/markets/search',
        title: 'Search',
        icon: 'fa-search'
      },
      {
        value: '/markets/create',
        title: 'Create',
        icon: this.ICON_CREATE,
        class: !this.navigationService.marketsManageEnabled ? 'disabled' : ''
      },
      {
        value: this.marketsModifyViewEnabled ? '/markets/modify' : '/markets/display',
        title: 'Display',
        icon: this.marketsModifyViewEnabled ? this.ICON_MODIFY : this.ICON_DISPLAY,
        class: !this.marketsDisplayViewEnabled ? 'disabled' : ''
      }
    ];

    navItems.push({
      value: NavigationMenuIdentifiers.MARKETS,
      title: 'Markets',
      icon: 'fa-store',
      isCollapsed: this.navigationService.isMarketsCollapsed,
      subMenus: marketsSubMenus
    });
  }

  private addFamiliesMenuItem(navItems: DfSideNavItem[]): void {
    if (!this.navigationService.familiesViewEnabled) {
      return;
    }

    const familiesSubMenus: DfSideNavItem[] = [];
    familiesSubMenus.push(this.buildHotelFamiliesSubMenu());
    familiesSubMenus.push(this.buildAirFamiliesSubMenu());

    navItems.push({
      value: NavigationMenuIdentifiers.FAMILIES,
      title: 'Families',
      icon: 'fa-desktop',
      isCollapsed: this.navigationService.isFamiliesCollapsed,
      subMenus: familiesSubMenus
    });
  }

  private buildHotelFamiliesSubMenu(): DfSideNavItem {
    const hotelFamiliesSubMenus: DfSideNavItem[] = [
      {
        value: RouteURL.familyHotelSearch,
        title: 'Search',
        icon: 'fa-search'
      },
      {
        value: RouteURL.familyHotelCreate,
        title: 'Create',
        icon: this.ICON_CREATE,
        class: !this.navigationService.familiesManageEnabled ? 'disabled' : ''
      },
      {
        value: this.familiesModifyViewEnabled ? RouteURL.familyHotelModify : RouteURL.familyHotelDisplay,
        title: 'Display',
        icon: this.familiesModifyViewEnabled ? this.ICON_MODIFY : this.ICON_DISPLAY,
        class: !this.familiesDisplayViewEnabled ? 'disabled' : ''
      }
    ];

    return {
      value: NavigationMenuIdentifiers.HOTEL_FAMILIES,
      title: 'Hotel Families',
      isCollapsed: this.navigationService.isHotelFamiliesCollapsed,
      subMenus: hotelFamiliesSubMenus
    };
  }

  private buildAirFamiliesSubMenu(): DfSideNavItem {
    const airFamiliesSubMenus: DfSideNavItem[] = [
      {
        value: RouteURL.familyAirSearch,
        title: 'Search',
        icon: 'fa-search'
      },
      {
        value: RouteURL.familyAirCreate,
        title: 'Create',
        icon: this.ICON_CREATE,
        class: !this.navigationService.familiesManageEnabled ? 'disabled' : ''
      },
      {
        value:
          this.lastActiveAirFamilyView === UppViewNames.MODIFY ? RouteURL.familyAirModify : RouteURL.familyAirDisplay,
        title: 'Display',
        icon: this.ICON_MODIFY,
        class: !this.lastActiveAirFamilyView ? 'disabled' : ''
      }
    ];

    return {
      value: NavigationMenuIdentifiers.AIR_FAMILIES,
      title: 'Air Families',
      isCollapsed: this.navigationService.isAirFamiliesCollapsed,
      subMenus: airFamiliesSubMenus
    };
  }

  private addFaresMenuItem(navItems: DfSideNavItem[]): void {
    if (!this.navigationService.faresViewEnabled) {
      return;
    }

    const faresSubMenus: DfSideNavItem[] = [
      {
        value: '/fares/search',
        title: 'Search',
        icon: 'fa-search'
      },
      {
        value: '/fares/create',
        title: 'Create',
        icon: this.ICON_CREATE,
        class: !this.navigationService.faresManageEnabled ? 'disabled' : ''
      },
      {
        value: this.faresModifyViewEnabled ? '/fares/modify' : '/fares/display',
        title: 'Display',
        icon: this.faresModifyViewEnabled ? this.ICON_MODIFY : this.ICON_DISPLAY,
        class: !this.faresDisplayViewEnabled ? 'disabled' : ''
      }
    ];

    navItems.push({
      value: NavigationMenuIdentifiers.FARES,
      title: 'Dynamic Exclusions',
      icon: 'fa-money-check-alt',
      isCollapsed: this.navigationService.isFaresCollapsed,
      subMenus: faresSubMenus
    });
  }

  private addFlightsMenuItem(navItems: DfSideNavItem[]): void {
    if (!this.navigationService.flightsViewEnabled) {
      return;
    }

    const flightsSubMenus: DfSideNavItem[] = [
      {
        value: '/flights/search',
        title: 'Search',
        icon: 'fa-search'
      },
      {
        value: '/flights/create',
        title: 'Create',
        icon: this.ICON_CREATE,
        class: !this.navigationService.flightsManageEnabled ? 'disabled' : ''
      },
      {
        value: this.flightsModifyViewEnabled ? '/flights/modify' : '/flights/display',
        title: 'Display',
        icon: this.flightsModifyViewEnabled ? this.ICON_MODIFY : this.ICON_DISPLAY,
        class: !this.flightsDisplayViewEnabled ? 'disabled' : ''
      }
    ];

    if (this.ruleHistoryActivate) {
      flightsSubMenus.push({
        value: '/flights/history',
        title: 'History',
        icon: 'fa-history',
        class: !this.navigationService.flightsViewEnabled ? 'disabled' : ''
      });
    }

    navItems.push({
      value: NavigationMenuIdentifiers.FLIGHTS,
      title: 'Flights',
      icon: 'fa-plane',
      isCollapsed: this.navigationService.isFlightsCollapsed,
      subMenus: flightsSubMenus
    });
  }

  private addHotelsMenuItem(navItems: DfSideNavItem[]): void {
    if (!this.navigationService.hotelsViewEnabled) {
      return;
    }

    const hotelsSubMenus: DfSideNavItem[] = [
      {
        value: '/hotels/search',
        title: 'Search',
        icon: 'fa-search'
      },
      {
        value: '/hotels/create',
        title: 'Create',
        icon: this.ICON_CREATE,
        class: !this.navigationService.hotelsManageEnabled ? 'disabled' : ''
      },
      {
        value: this.hotelsModifyViewEnabled ? '/hotels/modify' : '/hotels/display',
        title: 'Display',
        icon: this.hotelsModifyViewEnabled ? this.ICON_MODIFY : this.ICON_DISPLAY,
        class: !this.hotelsDisplayViewEnabled ? 'disabled' : ''
      }
    ];

    if (this.ruleHistoryActivate) {
      hotelsSubMenus.push({
        value: '/hotels/history',
        title: 'History',
        icon: 'fa-history',
        class: !this.navigationService.hotelsViewEnabled ? 'disabled' : ''
      });
    }

    navItems.push({
      value: NavigationMenuIdentifiers.HOTELS,
      title: 'Hotels',
      icon: 'fa-hotel',
      isCollapsed: this.navigationService.isHotelsCollapsed,
      subMenus: hotelsSubMenus
    });
  }

  private addAboutItem(navItems: DfSideNavItem[]): void {
    navItems.push({
      value: '/about',
      title: 'About',
      icon: 'fa-info-circle'
    });
  }
}
